import Bluebird from 'bluebird';
import { action, computed, makeObservable, observable, override, runInAction } from 'mobx';
import moment from 'moment';
import { stringify as toQueryStringParams } from 'query-string';
import { clearLaunchDarklyStorage } from '../../models/LaunchDarklyStorage';
import { getDisplayName } from '../../models/NameUtils';
import * as Api from '../sdk';
import { dateWithoutUTCTime, dateWithTimeToUTCString } from '../Utils';

export const RealMagicAccountIds = new Set([
	'b63aefc7-585a-4730-a33c-d680f4f87efc', // Production Levitate for Real Magic, Inc (named Real Magic, LLC in admin)
	'225afcd0-a708-4d3d-957d-9370f7c0bba6', // Test Levitate for Real Magic, Inc (named Levitate in admin)
]);

export const RealMagicAIDAAccountIds = new Set([
	'1702547b-cb86-41e4-8229-853b4e96a173', // Production AIDA for Real Magic, Inc
	'25c7088f-91d2-4ebb-b34b-bac9f9a25503', // Test AIDA for Real Magic, Inc
]);

export interface IUserSessionContext extends Api.IUserSession {
	isAuthenticated?: boolean;
	logout?(): void;
	updateWithCredential?(credential: Api.ICredential): Promise<Api.IUserSession>;
	webServiceHelper: Api.WebServiceHelper;
}

export interface IUserSessionContextConfig {
	apiConfig?: Api.IWebServiceHelperConfig;
}

export const DefaultUserSessionConfig: IUserSessionContextConfig = {
	apiConfig: { ...Api.DefaultConfig },
};

export enum MeetingDateRange {
	FutureDays,
	DateRange,
	Indefinitely,
}

export const daysOfWeekStrings = [
	{
		abbr: 'Sun',
		full: 'Sunday',
	},
	{
		abbr: 'Mon',
		full: 'Monday',
	},
	{
		abbr: 'Tues',
		full: 'Tuesday',
	},
	{
		abbr: 'Wed',
		full: 'Wednesday',
	},
	{
		abbr: 'Thur',
		full: 'Thursday',
	},
	{
		abbr: 'Fri',
		full: 'Friday',
	},
	{
		abbr: 'Sat',
		full: 'Saturday',
	},
];

export abstract class BaseViewModel<
	TUserSession extends UserSessionContext = UserSessionContext,
> extends Api.ImpersonationBroker {
	protected mUserSession: TUserSession;
	protected webServiceHelper: Api.WebServiceHelper;

	constructor(userSession?: TUserSession) {
		super();

		makeObservable<BaseViewModel, 'mUserSession'>(this, {
			mUserSession: observable.ref,
			userSession: computed,
			isAdmin: computed,
			isImpersonatingAccountAdmin: computed,
		});

		this.mUserSession = userSession;
		if (userSession) {
			this.webServiceHelper = userSession.webServiceHelper;
		}
	}

	public get userSession() {
		return this.mUserSession;
	}

	public set userSession(value: TUserSession) {
		runInAction(() => {
			this.mUserSession = value;
		});
	}

	public get isAdmin(): boolean {
		const role = (this instanceof UserSessionContext ? this : this.mUserSession)?.userRole?.toLocaleLowerCase();
		return role === 'admin' || role === 'superadmin';
	}

	/**
	 * Even if true, doesn't mean we have a valid user for the impersonation context. This vm is just allowed to do
	 * "admin"-level things.
	 */
	public get isImpersonatingAccountAdmin(): boolean {
		if (this.isImpersonating) {
			return this.impersonationContext.user
				? Api.isAdmin(this.impersonationContext.user)
				: !!this.impersonationContext.account;
		}
		return false;
	}
}

export class ViewModel<
	TUserSession extends UserSessionContext = UserSessionContext,
> extends BaseViewModel<TUserSession> {
	protected busy: boolean;
	protected loaded: boolean;
	public loading: boolean;

	constructor(userSession?: TUserSession) {
		super(userSession);

		makeObservable<ViewModel, 'busy' | 'loaded' | 'loading'>(this, {
			busy: observable,
			loaded: observable,
			loading: observable,
			isBusy: computed,
			isLoading: computed,
			isLoaded: computed,
			load: action,
		});

		this.loading = false;
		this.loaded = false;
		this.busy = false;
	}

	public get isBusy() {
		return this.busy || this.loading;
	}

	public get isLoading() {
		return this.loading;
	}

	public get isLoaded() {
		return this.loaded;
	}

	public load(): Promise<any> {
		return Promise.resolve(null);
	}
}

export class MeetingConfigViewModel extends ViewModel {
	private mMeetingConfig: Api.IMeetingConfig;
	private saving = false;
	private automationChoices: Api.IAutomationTemplate[] = [];
	public forUser: Api.IUser;

	public static INDEFINITELY_IN_DAYS = 1830;
	public static FUTURE_DAYS_TO_LOOK_AHEAD = 180;
	public static MAX_DATE_RANGE_DAYS = 730;

	constructor(userSession: UserSessionContext, config: Api.IMeetingConfigResponse, forUser?: Api.IUser) {
		super(userSession);

		makeObservable<MeetingConfigViewModel, 'mMeetingConfig' | 'saving' | 'automationChoices' | 'setForUser'>(this, {
			mMeetingConfig: observable,
			saving: observable,
			automationChoices: observable,
			availability: computed,
			confirmationNote: computed,
			duration: computed,
			id: computed,
			includeInEmailSignature: computed,
			isBusy: override,
			isSaving: computed,
			link: computed,
			locationConfig: computed,
			updateAllowInviteeLocation: action,
			deleteAllowInviteeLocation: action,
			getDefaultAddress: action,
			getDefaultVirtualLink: action,
			allowInviteePhone: computed,
			allowInviteeInPerson: computed,
			allowInviteeVirtual: computed,
			atLeastInterval: computed,
			atLeastDays: computed,
			setAtLeastInterval: action,
			atMostInterval: computed,
			atMostDays: computed,
			bufferAfterInterval: computed,
			bufferBeforeInterval: computed,
			setBufferBeforeInterval: action,
			setBufferAfterInterval: action,
			setAtMostInterval: action,
			dateRange: computed,
			setDateRange: action,
			updateCustomFieldMetaData: action,
			defaultDateRange: computed,
			meetingDateRadio: computed,
			name: computed,
			shortCode: computed,
			vanityPath: computed,
			ccEmails: computed,
			slotStartInMinutes: computed,
			isPublished: computed,
			showOnHomepage: computed,
			requireCompanyOnMainParticipant: computed,
			commentsRequired: computed,
			commentsLabel: computed,
			contactFormFields: computed,
			addMeetingNameToCalendarEvent: computed,
			maxPerDay: computed,
			maxPerMonth: computed,
			allowSameDay: computed,
			automationChoicesList: computed,
			meetingReminderTemplateId: computed,
			setAsEditing: action,
			setAvailability: action,
			commitChanges: action,
			discardChanges: action,
			updateProperty: action,
			getAutomationChoices: action,
			setConfig: action,
			setForUser: action,
		});

		this.setConfig(config);
		this.setForUser(forUser);
	}

	get availability() {
		return this.mMeetingConfig.draft?.availability ?? this.mMeetingConfig.published?.availability;
	}

	get baseRoute() {
		return `scheduler/config/${this.mMeetingConfig.id}`;
	}

	get confirmationNote() {
		return this.mMeetingConfig.draft?.confirmationNote ?? this.mMeetingConfig.published?.confirmationNote;
	}

	set confirmationNote(content: Api.IRawRichTextContentState) {
		this.updateProperty('confirmationnote', 'PUT', content);
	}

	get duration() {
		return this.mMeetingConfig.draft?.duration ?? this.mMeetingConfig.published?.duration;
	}

	set duration(duration: Api.IMeetingDuration) {
		this.updateProperty('duration', 'PUT', this.convertDurationToString(duration));
	}

	get id() {
		return this.mMeetingConfig.id;
	}

	get includeInEmailSignature() {
		return this.mMeetingConfig.draft?.includeInEmailSignature ?? this.mMeetingConfig.published?.includeInEmailSignature;
	}

	get isBusy() {
		return this.busy;
	}

	get isSaving() {
		return this.saving;
	}

	get link() {
		return this.mMeetingConfig.draft?.url ?? this.mMeetingConfig.published?.url;
	}

	get locationConfig() {
		return this.mMeetingConfig.draft?.locationConfig ?? this.mMeetingConfig.published?.locationConfig;
	}

	set locationConfig(config:
		| Api.IPhoneMeetingLocationConfig
		| Api.IAllowInviteeLocationConfig
		| Api.IOtherMeetingLocationConfig
		| Api.IInPersonLocationConfig
		| Api.IVirtualLocationConfig) {
		switch (config._type) {
			case Api.MeetingType.Phone:
				this.updateProperty('location/phone', 'PUT', config);
				break;
			case Api.MeetingType.AllowInvitee:
				this.updateProperty('location/allowInvitee', 'PUT', config);
				break;
			case Api.MeetingType.InPerson:
				this.updateProperty('location/inPerson', 'PUT', config);
				break;
			case Api.MeetingType.Virtual:
				this.updateProperty('location/virtual', 'PUT', config);
				break;
			default:
				break;
		}
	}

	public updateAllowInviteeLocation(
		location: Api.IPhoneMeetingLocation | Api.IInPersonMeetingLocation | Api.IVirtualMeetingLocation
	) {
		switch (location._type) {
			case Api.MeetingLocation.Phone:
				return this.updateProperty('location/allowInvitee/phone', 'PUT', location);
			case Api.MeetingLocation.InPerson:
				return this.updateProperty('location/allowInvitee/inPerson', 'PUT', location);
			case Api.MeetingLocation.Virtual:
				return this.updateProperty('location/allowInvitee/virtual', 'PUT', location);
			default:
				return null;
		}
	}

	public deleteAllowInviteeLocation(location: Api.MeetingLocation) {
		switch (location) {
			case Api.MeetingLocation.Phone:
				this.updateProperty('location/allowInvitee/phone', 'DELETE');
				break;
			case Api.MeetingLocation.InPerson:
				this.updateProperty('location/allowInvitee/inPerson', 'DELETE');
				break;
			case Api.MeetingLocation.Virtual:
				this.updateProperty('location/allowInvitee/virtual', 'DELETE');
				break;
			default:
				break;
		}
	}

	public getDefaultAddress() {
		if (this.locationConfig?._type === Api.MeetingType.InPerson) {
			return (this.locationConfig as Api.IInPersonLocationConfig).address;
		}
		return this.allowInviteeInPerson?.address || '';
	}

	public getDefaultVirtualLink() {
		if (this.locationConfig?._type === Api.MeetingType.Virtual) {
			return (this.locationConfig as Api.IVirtualLocationConfig).link;
		}
		return this.allowInviteeVirtual?.link || '';
	}

	get allowInviteePhone() {
		if (this.locationConfig?._type === Api.MeetingType.AllowInvitee) {
			return (this.locationConfig as Api.IAllowInviteeLocationConfig)?.locations?.find(
				x => x._type === Api.MeetingLocation.Phone
			);
		}
		return null;
	}

	get allowInviteeInPerson() {
		if (this.locationConfig?._type === Api.MeetingType.AllowInvitee) {
			return (this.locationConfig as Api.IAllowInviteeLocationConfig)?.locations?.find(
				x => x._type === Api.MeetingLocation.InPerson
			) as Api.IInPersonMeetingLocation;
		}
		return null;
	}

	get allowInviteeVirtual() {
		if (this.locationConfig?._type === Api.MeetingType.AllowInvitee) {
			return (this.locationConfig as Api.IAllowInviteeLocationConfig)?.locations?.find(
				x => x._type === Api.MeetingLocation.Virtual
			) as Api.IVirtualMeetingLocation;
		}
		return null;
	}

	get atLeastInterval() {
		return this.mMeetingConfig?.draft?.atLeastInterval ?? this.mMeetingConfig?.published?.atLeastInterval;
	}

	get atLeastDays() {
		return this.atLeastInterval ? parseInt(this.atLeastInterval, 10) : 0;
	}

	public async setAtLeastInterval(hours: number, days?: number) {
		const interval = `${days ? `${days}.` : ''}${String(hours).padStart(2, '0')}:00:00`;
		await this.updateProperty(`atLeastInterval?atLeastInterval=${interval}`, 'PUT');
	}

	get atMostInterval() {
		return this.mMeetingConfig?.draft?.atMostInterval ?? this.mMeetingConfig?.published?.atMostInterval;
	}

	get atMostDays() {
		return this.atMostInterval ? parseInt(this.atMostInterval, 10) : MeetingConfigViewModel.FUTURE_DAYS_TO_LOOK_AHEAD;
	}
	get bufferAfterInterval() {
		return this.mMeetingConfig?.draft?.bufferAfterInterval ?? this.mMeetingConfig?.published?.bufferAfterInterval;
	}

	get bufferBeforeInterval() {
		return this.mMeetingConfig?.draft?.bufferBeforeInterval ?? this.mMeetingConfig?.published?.bufferBeforeInterval;
	}

	get additionalHosts() {
		return this.mMeetingConfig?.draft?.additionalHosts ?? this.mMeetingConfig?.published?.additionalHosts;
	}

	public async setBufferBeforeInterval(interval: string) {
		await this.updateProperty(`BufferBeforeInterval?bufferBeforeInterval=${interval}`, 'PUT');
	}

	public async setBufferAfterInterval(interval: string) {
		await this.updateProperty(`BufferAfterInterval?bufferAfterInterval=${interval}`, 'PUT');
	}

	public async setAtMostInterval(atMostDays: number) {
		await this.updateProperty('dateRange', 'DELETE');
		await this.updateProperty(`atMostInterval?atMostDays=${encodeURIComponent(atMostDays)}`, 'PUT');
	}

	get dateRange() {
		return this.mMeetingConfig?.draft?.range ?? this.mMeetingConfig?.published?.range;
	}

	public setDateRange(start: Date, end: Date) {
		this.updateProperty(
			`dateRange?start=${encodeURIComponent(dateWithTimeToUTCString(start, 0, 0, 0))}&end=${encodeURIComponent(
				dateWithTimeToUTCString(end, 23, 59, 59)
			)}`,
			'PUT'
		);
	}

	public updateCustomFieldMetaData(fieldName: string, include: boolean, require: boolean) {
		this.updateProperty(
			`CustomField/${encodeURIComponent(fieldName)}?include=${encodeURIComponent(include)}&require=${encodeURIComponent(
				require
			)}`,
			'PUT'
		);
	}

	get defaultDateRange() {
		if (this.dateRange?.start && this.dateRange?.end) {
			const start = dateWithoutUTCTime(this.dateRange.start);
			const end = dateWithoutUTCTime(this.dateRange.end);
			return {
				from: start,
				to: end,
			};
		}
		const endDate = new Date();
		endDate.setDate(endDate.getDate() + 14);
		return { from: new Date(), to: endDate };
	}

	get meetingDateRadio() {
		if (this.dateRange?.start && this.dateRange?.end) {
			return MeetingDateRange.DateRange;
		}
		return this.atMostDays === MeetingConfigViewModel.INDEFINITELY_IN_DAYS
			? MeetingDateRange.Indefinitely
			: MeetingDateRange.FutureDays;
	}

	get name() {
		return this.mMeetingConfig.draft?.name ?? this.mMeetingConfig.published?.name;
	}

	set name(name: string) {
		this.updateProperty(`name?name=${encodeURIComponent(name)}`, 'PUT');
	}

	get shortCode() {
		return this.mMeetingConfig.draft?.shortCode ?? this.mMeetingConfig.published?.shortCode;
	}

	get vanityPath() {
		return this.mMeetingConfig.draft?.vanityPath ?? this.mMeetingConfig.published?.vanityPath;
	}

	get ccEmails() {
		return this.mMeetingConfig.draft?.ccEmails ?? this.mMeetingConfig.published?.ccEmails;
	}

	set ccEmails(CcEmails: string[]) {
		this.updateProperty(`CcEmails`, 'PUT', CcEmails);
	}

	get slotStartInMinutes() {
		return this.mMeetingConfig.draft?.slotStartInMinutes ?? this.mMeetingConfig?.published?.slotStartInMinutes;
	}

	set slotStartInMinutes(SlotStartInMinutes: number[]) {
		this.updateProperty(`SlotStartInMinutes`, 'PUT', SlotStartInMinutes);
	}

	get isPublished() {
		return !!this.mMeetingConfig?.published;
	}

	get showOnHomepage() {
		return this.mMeetingConfig.draft?.showOnHomepage ?? this.mMeetingConfig.published?.showOnHomepage;
	}

	set showOnHomepage(showOnHomepage: boolean) {
		this.updateProperty(`showOnHomepage?showOnHomepage=${encodeURIComponent(showOnHomepage)}`, 'PUT');
	}

	get requireCompanyOnMainParticipant() {
		return (
			this.mMeetingConfig.draft?.requireCompanyOnMainParticipant ??
			this.mMeetingConfig.published?.requireCompanyOnMainParticipant
		);
	}

	set requireCompanyOnMainParticipant(value: boolean) {
		this.updateProperty(
			`RequireCompanyOnMainParticipant?RequireCompanyOnMainParticipant=${encodeURIComponent(value)}`,
			'PUT'
		);
	}

	get commentsRequired() {
		return this.mMeetingConfig.draft?.commentsRequired ?? this.mMeetingConfig.published?.commentsRequired;
	}

	set commentsRequired(value: boolean) {
		this.updateProperty(`CommentsRequired?commentsRequired=${encodeURIComponent(value)}`, 'PUT');
	}

	get commentsLabel() {
		return this.mMeetingConfig.draft?.commentsLabel ?? this.mMeetingConfig.published?.commentsLabel;
	}

	set commentsLabel(value: string) {
		this.updateProperty(`CommentsLabel?commentsLabel=${encodeURIComponent(value)}`, 'PUT');
	}

	get contactFormFields() {
		return this.mMeetingConfig.draft?.contactFormFields ?? this.mMeetingConfig.published?.contactFormFields;
	}

	get addMeetingNameToCalendarEvent() {
		return (
			this.mMeetingConfig.draft?.addMeetingNameToCalendarEvent ??
			this.mMeetingConfig.published?.addMeetingNameToCalendarEvent
		);
	}

	set addMeetingNameToCalendarEvent(addMeetingNameToCalendarEvent: boolean) {
		this.updateProperty(
			`addMeetingNameToCalendarEvent?addMeetingNameToCalendarEvent=${encodeURIComponent(
				addMeetingNameToCalendarEvent
			)}`,
			'PUT'
		);
	}

	get maxPerDay() {
		return this.mMeetingConfig.draft?.maxPerDay ?? this.mMeetingConfig.published?.maxPerDay;
	}

	get maxPerMonth() {
		return this.mMeetingConfig.draft?.maxPerMonth ?? this.mMeetingConfig.published?.maxPerMonth;
	}

	get allowSameDay() {
		return this.atLeastInterval === '00:00:00';
	}

	set allowSameDay(allowSameDay: boolean) {
		this.updateProperty(`allowSameDay?allowSameDay=${encodeURIComponent(allowSameDay)}`, 'PUT');
	}

	get automationChoicesList() {
		return this.automationChoices;
	}

	get meetingReminderTemplateId() {
		return (
			this.mMeetingConfig.draft?.meetingReminderTemplateId ?? this.mMeetingConfig.published?.meetingReminderTemplateId
		);
	}

	set meetingReminderTemplateId(id: string) {
		this.updateProperty(`meetingReminderTemplateId?meetingReminderTemplateId=${encodeURIComponent(id)}`, 'PUT');
	}

	public async setAsEditing() {
		if (!this.busy) {
			this.busy = true;
			try {
				const opResult = await this.userSession.webServiceHelper.callWebServiceAsync(
					this.composeApiUrl({ urlPath: `${this.baseRoute}/edit` }),
					'POST'
				);

				if (opResult.success) {
					this.setConfig(opResult.value);
				} else {
					throw Api.asApiError(opResult);
				}

				this.busy = false;
			} catch (err) {
				this.busy = false;
				throw Api.asApiError(err);
			}
		}
	}

	public setAvailability(day: Api.DayOfWeek, availability?: Api.IDailyIntervals) {
		return this.updateProperty(
			`availability/${daysOfWeekStrings[day].full.toLocaleLowerCase()}`,
			availability ? 'PUT' : 'DELETE',
			availability
		);
	}

	public async setAdditionalHosts(additionalHosts: Api.IUserReference[]) {
		await this.updateProperty(
			'AdditionalHostIds',
			'PUT',
			additionalHosts.map(x => x.id)
		);
	}

	public async commitChanges() {
		if (!this.busy) {
			this.busy = true;
			try {
				const opResult = await this.userSession.webServiceHelper.callWebServiceAsync(
					this.composeApiUrl({
						queryParams: {
							forUserId: this.forUser?.id,
						},
						urlPath: `${this.baseRoute}/commit`,
					}),
					'POST'
				);

				if (opResult.success) {
					this.setConfig(opResult.value);
				} else {
					throw Api.asApiError(opResult);
				}

				this.busy = false;
			} catch (err) {
				this.busy = false;
				throw Api.asApiError(err);
			}
		}
	}

	public async discardChanges() {
		if (!this.busy) {
			this.busy = true;
			try {
				const opResult = await this.userSession.webServiceHelper.callWebServiceAsync(
					this.composeApiUrl({
						queryParams: {
							forUserId: this.forUser?.id,
						},
						urlPath: `${this.baseRoute}/discard`,
					}),
					'POST'
				);

				if (opResult.success) {
					this.setConfig(opResult.value);
				} else {
					throw Api.asApiError(opResult);
				}

				this.busy = false;
			} catch (err) {
				this.busy = false;
				throw Api.asApiError(err);
			}
		}
	}

	public updateProperty(url: string, method: Api.HTTPMethod, body: any = null) {
		if (!this.saving) {
			// Only set saving to true if the url is not name
			// avoid race conditions
			if (!url.includes('name')) {
				this.saving = true;
			}

			const forUserIdParam = this.forUser?.id ? `${url.includes('?') ? '&' : '?'}forUserId=${this.forUser?.id}` : '';
			return this.userSession.webServiceHelper
				.callWebServiceAsync(
					this.composeApiUrl({
						urlPath: `${this.baseRoute}/${url}${forUserIdParam}`,
					}),
					method,
					body
				)
				.then(result => {
					if (result.success) {
						runInAction(() => {
							this.setConfig(result.value);
							this.saving = false;
						});
					} else {
						runInAction(() => {
							this.saving = false;
						});
						throw Api.asApiError(result);
					}
				})
				.catch((err: Api.IOperationResultNoValue) => {
					runInAction(() => {
						this.saving = false;
					});
					throw Api.asApiError(err);
				});
		}

		return null;
	}

	public async getAutomationChoices() {
		try {
			const data: Api.IOperationResult<Api.IAutomationTemplate[]> =
				await this.userSession.webServiceHelper.callWebServiceAsync(
					this.composeApiUrl({ urlPath: `automationTemplate/meetingReminders` }),
					'GET'
				);
			this.automationChoices = data.value;
		} catch (err) {
			throw Api.asApiError(err);
		}
	}

	/**
	 * Parses the duration string in format: d.hh:mm:ss into Api.IMeetingDuration format.
	 *
	 * @param {string} durationString
	 * @returns {Api.IMeetingDuration}
	 */
	private convertDurationFromString(durationString: string) {
		let rest = durationString;
		const parsed: Api.IMeetingDuration = {
			days: 0,
			hours: 0,
			minutes: 0,
			seconds: 0,
		};

		if (durationString.includes('.')) {
			const [days, r] = durationString.split('.');
			parsed.days = parseInt(days, 10);
			rest = r;
		}

		const [h, m, s] = rest.split(':');
		return {
			...parsed,
			hours: parseInt(h, 10),
			minutes: parseInt(m, 10),
			seconds: parseInt(s, 10),
		};
	}

	/**
	 * Converts an Api.IMeetingDuration to a string in format d.hh:mm:ss
	 *
	 * @param {Api.IMeetingDuration} duration
	 * @returns {string}
	 */
	private convertDurationToString(duration: Api.IMeetingDuration) {
		const { days, hours, minutes, seconds } = duration;
		const d = days <= 9 ? `0${days}` : days;
		const h = hours <= 9 ? `0${hours}` : hours;
		const m = minutes <= 9 ? `0${minutes}` : minutes;
		const s = seconds <= 9 ? `0${seconds}` : seconds;
		return `${d}.${h}:${m}:${s}`;
	}

	public setConfig(configResponse: Api.IMeetingConfigResponse) {
		const config: Partial<Api.IMeetingConfig> = {};

		Object.entries(configResponse).forEach(([key, value]) => {
			let duration: Api.IMeetingDuration;
			switch (key) {
				case 'draft':
					duration = this.convertDurationFromString(value.duration);
					config.draft = { ...value, duration };
					break;
				case 'published':
					duration = this.convertDurationFromString(value.duration);
					config.published = { ...value, duration };
					break;
				default:
					// FOLLOWUP: Resolve

					// @ts-ignore
					config[key] = value;
					break;
			}
		});

		this.mMeetingConfig = config;
	}

	private setForUser(forUser: Api.IUser) {
		this.forUser = forUser;
	}
}

export class UserViewModel<TUser extends Api.IUser = Api.IUser> extends ViewModel {
	protected deleting: boolean;
	protected mMeetingConfigs: MeetingConfigViewModel[];
	protected mUser: TUser;
	protected mUploadingProfilePic = false;
	protected loadingPromise: Promise<TUser>;

	constructor(userSession: UserSessionContext, user: TUser) {
		super(userSession);

		makeObservable<UserViewModel<TUser>, 'deleting' | 'mMeetingConfigs' | 'mUser' | 'mUploadingProfilePic'>(this, {
			deleting: observable,
			mMeetingConfigs: observable,
			mUser: observable,
			mUploadingProfilePic: observable,
			uploadingProfilePic: computed,
			firstName: computed,
			lastName: computed,
			name: computed,
			email: computed,
			connectedEmailAddress: computed,
			userMilestones: computed,
			emailProvider: computed,
			preferences: computed,
			role: computed,
			profilePicUrl: computed,
			groups: computed,
			contactId: computed,
			activationStatus: computed,
			accountId: computed,
			id: computed,
			isDeleting: computed,
			isBusy: override,
			meetingConfigs: computed,
			socialMediaConnectedAccounts: computed,
			load: override,
			setUser: action,
			toggleAdminUserRole: action,
			toggleActive: action,
			delete: action,
			update: action,
			updateUserPreferences: action,
			updateKeepInTouchCommitmentPreferences: action,
			updateUnsubscribeTemplateId: action,
			updateTextAutoReplySettings: action,
		});

		this.mSetUser(user);
	}

	public get uploadingProfilePic() {
		return this.mUploadingProfilePic;
	}

	public get firstName() {
		return this.mUser.firstName;
	}

	public get lastName() {
		return this.mUser.lastName;
	}

	public get name() {
		return getDisplayName(this.mUser);
	}

	public get email() {
		return this.mUser.email;
	}

	public get connectedEmailAddress() {
		return this.mUser.connectedEmailAddress;
	}

	public get userMilestones() {
		return this.mUser.userMilestones;
	}

	public get emailProvider() {
		return this.mUser.emailProvider;
	}

	public get preferences() {
		return this.mUser.userPreferences;
	}

	public get role() {
		return this.mUser.role;
	}

	public get profilePicUrl() {
		return this.mUser.profilePic;
	}

	public get groups() {
		return this.mUser.groups;
	}

	public get contactId() {
		return this.mUser.contactId;
	}

	public get activationStatus() {
		return this.mUser.activationStatus;
	}

	public get accountId() {
		return this.mUser.accountId;
	}

	public get id() {
		return this.mUser.id;
	}

	public get isDeleting() {
		return this.deleting;
	}

	public get isBusy() {
		return this.loading || this.busy || this.deleting;
	}

	public get meetingConfigs() {
		return this.mMeetingConfigs || [];
	}

	public get socialMediaConnectedAccounts() {
		return this.mUser.socialMediaConnectedAccounts;
	}

	public load(force = false) {
		if (this.isLoaded && !force) {
			return Promise.resolve(this.mUser);
		}

		if (!this.loadingPromise) {
			this.loading = true;
			this.loadingPromise = new Promise<TUser>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<TUser>) => {
					runInAction(() => {
						this.loading = false;
						this.loadingPromise = null;
						if (result.success) {
							this.mSetUser(result.value);
							this.loaded = true;
							resolve(result.value);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<TUser>(
					this.composeApiUrl({ urlPath: `user/${this.mUser.id}` }),
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
		}

		return this.loadingPromise;
	}

	public setUser = (user: TUser) => {
		this.mSetUser(user);
	};

	public toggleAdminUserRole = (makeAdmin: boolean) => {
		if (makeAdmin === (this.mUser.groups && this.mUser.groups.findIndex(x => x === 'admin') >= 0)) {
			return Promise.resolve<Api.IUser>(this.mUser);
		}

		if (!this.busy) {
			this.busy = true;
			return new Promise<TUser>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<TUser>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							this.mSetUser(result.value);
							this.loaded = true;
							resolve(result.value);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<TUser>(
					this.composeApiUrl({ urlPath: `user/${this.mUser.id}/${makeAdmin ? 'makeAdmin' : 'removeAdmin'}` }),
					'POST',
					null,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public toggleActive = (active: boolean) => {
		if (active === (this.mUser.activationStatus === 'ACTIVE' ? true : false)) {
			return Promise.resolve<TUser>(this.mUser);
		}

		if (!this.busy) {
			this.busy = true;
			return new Promise<TUser>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<TUser>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							this.mSetUser(result.value);
							this.loaded = true;
							resolve(result.value);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<TUser>(
					this.composeApiUrl({ urlPath: `user/${this.mUser.id}/${active ? 'reactivate' : 'deactivate'}` }),
					'POST',
					null,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public delete = () => {
		if (!this.isBusy) {
			this.deleting = true;
			return new Promise<TUser>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<TUser>) => {
					runInAction(() => {
						this.deleting = false;
						if (result.success) {
							resolve(result.value);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<TUser>(
					this.composeApiUrl({ urlPath: `user/${this.mUser.id}` }),
					'DELETE',
					null,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public update = (user: TUser) => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<TUser>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<TUser>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							this.mSetUser(result.value);
							resolve(result.value);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<TUser>(
					this.composeApiUrl({ urlPath: `user/${this.mUser.id}` }),
					'PUT',
					user,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public updateUserPreferences = (userPreferences: Api.IUserPreferences) => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.IUserPreferences>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IUserPreferences>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							this.mUser.userPreferences = result.value;
							this.userSession.user.userPreferences = result.value;
							resolve(result.value);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IUserPreferences>(
					this.composeApiUrl({ urlPath: 'user/preferences' }),
					'PUT',
					userPreferences,
					onFinish,
					onFinish
				);
			});
		}
		return null;
	};

	public updateKeepInTouchCommitmentPreferences = (kitCommitmentPreferences: Api.IKeepInTouchCommitmentPreferences) => {
		if (!this.isBusy) {
			this.busy = true;
			return new Promise<Api.IKeepInTouchCommitmentPreferences>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IKeepInTouchCommitmentPreferences>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							this.mUser.userPreferences.keepInTouchCommitmentPreferences = result.value;
							resolve(result.value);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IKeepInTouchCommitmentPreferences>(
					this.composeApiUrl({ urlPath: `user/${this.mUser.id}/keepInTouchCommitmentPreferences` }),
					'PUT',
					kitCommitmentPreferences,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public updateUnsubscribeTemplateId = (templateId: string) => {
		if (!this.busy) {
			this.busy = true;
			return new Promise<Api.IOperationResult<TUser>>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<TUser>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							this.mSetUser(result.value);
							resolve(result);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults(
					this.composeApiUrl({
						queryParams: {
							templateId,
						},
						urlPath: 'user/preferences/unsubscribeTemplate',
					}),
					'POST',
					null,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public updateTextAutoReplySettings = (textAutoReply: Api.ITextAutoReply) => {
		if (!this.busy) {
			this.busy = true;
			return new Promise<Api.IOperationResult<TUser>>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<TUser>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							this.mSetUser(result.value);
							this.userSession.user.textAutoReply = result.value.textAutoReply;
							resolve(result);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults(
					this.composeApiUrl({ urlPath: 'user/textAutoReply' }),
					'PUT',
					textAutoReply,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public toJs = () => {
		return this.mUser;
	};

	protected mSetUser = (user: TUser) => {
		this.mUser = user;
	};

	public get enabledAuthenticators() {
		return this.mUser.enabledAuthenticators;
	}
}

export class AccountViewModel extends ViewModel {
	private saving: boolean;
	protected mAccount: Api.IAccount;
	private mUsersPageCollectionController: ObservablePageCollectionControllerOld<Api.IUser, UserViewModel>;
	private loadingPromise: Promise<Api.IAccount>;

	constructor(userSession: UserSessionContext, account: Api.IAccount) {
		super(userSession);

		makeObservable<AccountViewModel, 'saving' | 'mAccount'>(this, {
			saving: observable,
			mAccount: observable.ref,
			isPaid: computed,
			isTrial: computed,
			integrations: computed,
			isFetchingUsers: computed,
			id: computed,
			additionalInfo: computed,
			planDetails: computed,
			emailDomain: computed,
			creationDate: computed,
			activationStatus: computed,
			emailProviderConfiguration: computed,
			companyName: computed,
			features: computed,
			preferences: computed,
			reseller: computed,
			isSaving: computed,
			isBusy: override,
			isBrokerDealer: computed,
			isLevitateSalesCoffeeAccount: computed,
			isRealMagicAccount: computed,
			reset: action,
			load: override,
			save: action,
			getUserCount: action,
			updateUnsubscribeTemplateId: action,
			updateHtmlNewsletterFeatures: action,
			updateMeetingSchedulerFeatures: action,
			setIntegration: action,
			saveStripeKeys: action,
			removeStripeKeys: action,
			saveStripePaymentFees: action,
			getAccountPublicKey: action,
			getUsers: action,
		});

		this.mAccount = account;
		this.mUsersPageCollectionController = new ObservablePageCollectionControllerOld<Api.IUser, UserViewModel>(
			userSession.webServiceHelper,
			'user',
			null,
			this.createUserViewModel
		);
	}

	public get isBrokerDealer() {
		return this.mAccount.preferences?.accountCategory === Api.AccountCategory.BrokerDealer;
	}

	public get isPersonalAccount() {
		return this.userSession?.account?.planDetails?.planId === 0;
	}

	public get isPaid() {
		return this.mAccount.planDetails.billingType === Api.BillingType.Paid;
	}

	public get isTrial() {
		return this.mAccount.planDetails.billingType === Api.BillingType.Trial;
	}

	public get integrations() {
		return this.mAccount.integrations;
	}

	public get isFetchingUsers() {
		return this.mUsersPageCollectionController.fetching;
	}

	public get id() {
		return this.mAccount.id;
	}

	public get additionalInfo() {
		return this.mAccount?.additionalInfo;
	}

	public get planDetails() {
		return this.mAccount.planDetails;
	}

	public get emailDomain() {
		return this.mAccount.emailDomain;
	}

	public get creationDate() {
		return this.mAccount.creationDate;
	}

	public get activationStatus() {
		return this.mAccount.activationStatus;
	}

	public get emailProviderConfiguration() {
		return this.mAccount.emailProviderConfigurations;
	}

	public get companyName() {
		return this.mAccount.companyName;
	}

	public get requireSecondAuthFactor() {
		return this.mAccount.requireSecondAuthFactor;
	}

	public get oAuthLoginSettings() {
		return this.mAccount.oAuthLoginSettings;
	}

	public get features() {
		return this.mAccount.features;
	}

	public get preferences() {
		return this.mAccount.preferences;
	}

	public get reseller() {
		return this.mAccount.reseller;
	}

	public get isSaving() {
		return this.saving;
	}

	public get isBusy() {
		return this.loading || this.busy || this.saving || this.mUsersPageCollectionController.fetching;
	}

	public get isLevitateSalesCoffeeAccount() {
		return RealMagicAIDAAccountIds.has(this.id);
	}

	public get isRealMagicAccount() {
		return RealMagicAccountIds.has(this.id);
	}

	public get stripePayment() {
		return this.mAccount?.features?.stripePayment;
	}

	public reset = () => {
		this.mUsersPageCollectionController.reset();
		this.loadingPromise = null;
		this.loading = false;
		this.busy = false;
		this.saving = false;
	};

	public load(force = false) {
		if (this.isLoaded && !force) {
			return Promise.resolve(this.mAccount);
		}

		if (!this.loadingPromise) {
			this.loading = true;
			this.loadingPromise = new Promise<Api.IAccount>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IAccount>) => {
					runInAction(() => {
						this.loading = false;
						this.loadingPromise = null;
						if (result.success) {
							this.mAccount = result.value;
							this.loaded = true;
							resolve(result.value);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccount>(
					`account/${this.mAccount.id}`,
					'GET',
					null,
					onFinish,
					onFinish
				);
			});
		}

		return this.loadingPromise;
	}

	public save = (account: Api.IAccount) => {
		if (!this.saving) {
			this.saving = true;
			const promise = new Promise<Api.IAccount>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IAccount>) => {
					runInAction(() => {
						this.saving = false;
						if (result.success) {
							this.mAccount = result.value;
							this.loaded = true;
							resolve(result.value);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IAccount>(
					`account/${this.mAccount.id}`,
					'PUT',
					account,
					onFinish,
					onFinish
				);
			});
			return promise;
		}

		return Promise.resolve(null);
	};

	public saveStripeKeys = (stripeKeys: Api.IStripeKeys) => {
		if (!this.saving) {
			this.saving = true;
			const promise = new Promise<Api.IStripeKeys>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IStripeKeys>) => {
					runInAction(() => {
						this.saving = false;
						if (result.success) {
							resolve(result.value);
							return;
						}
						reject(result);
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IStripeKeys>(
					`stripe/auth/api-keys`,
					'POST',
					stripeKeys,
					onFinish,
					onFinish
				);
			});
			return promise;
		}

		return Promise.resolve(null);
	};

	public saveStripePaymentFees = (stripePaymentFees: {
		cardFeePercentage: number;
		cardFeeFixed: number;
		customerPortalUrl?: string;
	}) => {
		if (!this.saving) {
			this.saving = true;
			const promise = new Promise<Api.IStripePaymentsFeature>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IStripePaymentsFeature>) => {
					runInAction(() => {
						this.saving = false;
						if (result.success) {
							resolve(result.value);
							return;
						}
						reject(result);
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.IStripePaymentsFeature>(
					`account/features/stripe/configuration`,
					'PUT',
					stripePaymentFees,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
		return Promise.resolve(null);
	};

	public updateSendEmailFeatures = (sendEmailFeatures: Partial<Api.ISendEmailFeatures>) => {
		if (!this.saving) {
			this.saving = true;
			const promise = new Promise<Api.ISendEmailFeatures>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.ISendEmailFeatures>) => {
					runInAction(() => {
						this.saving = false;
						if (result.success) {
							// Update the account's sendEmail features
							this.mAccount = {
								...this.mAccount,
								features: {
									...this.mAccount.features,
									sendEmail: result.value,
								},
							};
							resolve(result.value);
							return;
						}
						reject(result);
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<Api.ISendEmailFeatures>(
					`account/features/sendEmail`,
					'PUT',
					sendEmailFeatures,
					onFinish,
					onFinish
				);
			});
			return promise;
		}

		return Promise.resolve(null);
	};

	public removeStripeKeys = () => {
		if (!this.saving) {
			this.saving = true;
			const promise = new Promise<boolean>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<boolean>) => {
					runInAction(() => {
						this.saving = false;
						if (result.success) {
							resolve(result.value);
							return;
						}
						reject(result);
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<boolean>(
					`stripe/auth/api-keys`,
					'DELETE',
					null,
					onFinish,
					onFinish
				);
			});
			return promise;
		}
		return Promise.resolve(null);
	};

	public getAccountPublicKey = () => {
		return new Promise<Api.IOperationResult<string>>((resolve, reject) => {
			const onFinish = (result: Api.IOperationResult<string>) => {
				if (result.success) {
					resolve(result);
					return;
				}
				reject(result);
			};
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<string>(
				`stripe/auth/publishable-key`,
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
	};

	public getUserCount = () => {
		return new Promise<number>((resolve, reject) => {
			const onFinish = (result: Api.IOperationResult<number>) => {
				if (result.success) {
					resolve(result.value);
				} else {
					reject(result);
				}
			};
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<number>(
				`account/${this.mAccount.id}/userCount`,
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
	};

	public updateUnsubscribeTemplateId = (templateId: string) => {
		if (!this.busy) {
			this.busy = true;
			return new Promise<Api.IOperationResult<Api.IAccount>>((resolve, reject) => {
				const onFinish = (result: Api.IOperationResult<Api.IAccount>) => {
					runInAction(() => {
						this.busy = false;
						if (result.success) {
							this.mAccount = result.value;
							resolve(result);
						} else {
							reject(result);
						}
					});
				};
				this.mUserSession.webServiceHelper.callWebServiceWithOperationResults(
					`account/preferences/unsubscribeTemplateId?templateId=${encodeURIComponent(templateId)}`,
					'POST',
					null,
					onFinish,
					onFinish
				);
			});
		}

		return null;
	};

	public async updateHtmlNewsletterFeatures(features: Api.IHtmlNewsletterFeatures) {
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IHtmlNewsletterFeatures>(
				`account/features/htmlNewsletter`,
				'PUT',
				features
			);
			if (opResult.success) {
				this.mAccount = {
					...this.mAccount,
					features: {
						...this.mAccount.features,
						htmlNewsletter: opResult.value,
					},
				};
			}
			this.busy = false;
			return opResult;
		}
	}

	public async updateMeetingSchedulerFeatures(features: Api.IFeature) {
		if (!this.isBusy) {
			this.busy = true;
			const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IFeature>(
				`account/features/meetingScheduler`,
				'PUT',
				features
			);
			if (opResult.success) {
				this.mAccount = {
					...this.mAccount,
					features: {
						...this.mAccount.features,
						meetingScheduler: opResult.value,
					},
				};
			}
			this.busy = false;
			return opResult;
		}
	}

	public setIntegration<T extends Api.IConfigurableIntegration>(key: keyof Api.IAccountIntegrations, integration: T) {
		this.mAccount = {
			...this.mAccount,
			integrations: {
				...(this.mAccount.integrations || {}),
				[key]: integration,
			},
		};
	}

	public getUsers = (sortDescriptor?: Api.ISortDescriptor, pageSize?: number, params?: any) => {
		return this.mUsersPageCollectionController.getNext(sortDescriptor, pageSize, params);
	};

	public toJs = () => {
		return this.mAccount;
	};

	private createUserViewModel = (userModel: Api.IUser) => {
		return new UserViewModel(this.mUserSession, userModel);
	};

	public setIngration<T extends Api.IConfigurableIntegration>(key: keyof Api.IAccountIntegrations, integration: T) {
		this.mAccount = {
			...this.mAccount,
			integrations: {
				...(this.mAccount.integrations || {}),
				[key]: integration,
			},
		};
	}

	public getSelfServicePaymentUrl = (accountId: string): Promise<Api.IOperationResult<string>> => {
		return new Promise<Api.IOperationResult<string>>((resolve, reject) => {
			const onFinish = (opResult: Api.IOperationResult<string>) => {
				if (opResult.success) {
					resolve(opResult);
				} else {
					reject(opResult);
				}
			};
			this.mUserSession.webServiceHelper.callWebServiceWithOperationResults<string>(
				`account/${encodeURIComponent(accountId)}/selfServicePaymentUrl`,
				'GET',
				null,
				onFinish,
				onFinish
			);
		});
	};
}

export class UserSessionContext extends ViewModel implements IUserSessionContext {
	protected requestCount: number;
	private mAccount: AccountViewModel;
	protected mApiUserSession: Api.IUserSession;
	protected mSupportedPluginFeatures: Api.ISupportedPluginFeatures;
	protected config: IUserSessionContextConfig;
	public declare webServiceHelper: Api.WebServiceHelper;

	constructor(config?: IUserSessionContextConfig) {
		super();

		makeObservable<
			UserSessionContext,
			| 'requestCount'
			| 'mAccount'
			| 'mApiUserSession'
			| 'mSupportedPluginFeatures'
			| 'checkAndSetTimezone'
			| 'getUserSessionWithCredential'
			| 'onRequestStart'
			| 'onRequestEnd'
		>(this, {
			requestCount: observable,
			mAccount: observable.ref,
			mApiUserSession: observable.ref,
			mSupportedPluginFeatures: observable.ref,
			emailProviderUsesBasicCredentials: computed,
			account: computed,
			apiRequestCount: computed,
			isAdmin: override,
			isBusy: override,
			isAuthenticated: computed,
			user: computed,
			telephonyConfiguration: computed,
			coffeeKeyFieldMappings: computed,
			defaultSendOnBehalfPermissions: computed,
			pendingActions: computed,
			pendingAccountActions: computed,
			featureFlags: computed,
			newFeatures: computed,
			customSendOnBehalfPermissions: computed,
			isCustomerImpersonation: computed,
			canAccessSupport: computed,
			sessionReplayOptOut: computed,
			canSendOnBehalf: computed,
			canSendOnBehalfWithoutFilters: computed,
			levitateUIStimulant: computed,
			supportedPluginFeatures: computed,
			setSupportedPluginFeatures: action,
			resolvePendingAction: action,
			clearPendingActions: action,
			removePendingAccountAction: action,
			userRole: computed,
			hasAnyEmailPendingActions: computed,
			needsToReconnectEmail: computed,
			needsEmailConnect: computed,
			sendOnBehalfIsEnabled: computed,
			updateWithCredential: action,
			logout: action,
			load: override,
			configuredIntegrations: computed,
			hasInsuranceIntegrationConfigured: computed,
			hasNonProfitIntegrationConfigured: computed,
			hasLegalIntegrationConfigured: computed,
			checkAndSetTimezone: action,
			getUserSessionWithCredential: action,
			onRequestStart: action,
			onRequestEnd: action,
		});

		this.setUserSession = this.setUserSession.bind(this);
		this.getStoredCredential = this.getStoredCredential.bind(this);
		this.logout = this.logout.bind(this);
		this.requestCount = 0;
		this.config = { ...DefaultUserSessionConfig, ...config };
		this.webServiceHelper = new Api.WebServiceHelper(this.config.apiConfig);
		this.webServiceHelper.on(Api.WebServiceHelper.EventNames.REQUEST_START, this.onRequestStart);
		this.webServiceHelper.on(Api.WebServiceHelper.EventNames.REQUEST_END, this.onRequestEnd);
	}

	public get emailProviderUsesBasicCredentials() {
		const emailProvider =
			this.mApiUserSession && this.mApiUserSession.user ? this.mApiUserSession.user.emailProvider : null;
		if (emailProvider) {
			return emailProvider === 'Exchange' || emailProvider === 'Imap';
		}
		return false;
	}

	public get account() {
		return this.mAccount;
	}

	public get apiRequestCount() {
		return this.requestCount;
	}

	public get isBusy() {
		return this.loading || this.busy;
	}

	public get isAuthenticated() {
		return !!this.mApiUserSession;
	}

	public get user() {
		return this.isAuthenticated ? this.mApiUserSession.user : null;
	}

	public get telephonyConfiguration() {
		return this.mApiUserSession?.telephonyConfiguration;
	}

	public get coffeeKeyFieldMappings() {
		return this.mApiUserSession?.coffeeKeyFieldMappings;
	}

	public get pendingActions() {
		return (this.mApiUserSession || {}).pendingActions || [];
	}

	public get pendingAccountActions() {
		return (this.mApiUserSession || {}).pendingAccountActions || [];
	}

	public get featureFlags() {
		return (this.mApiUserSession || {}).featureFlags || [];
	}

	public get newFeatures() {
		return (this.mApiUserSession || {}).newFeatures || [];
	}

	public get isCustomerImpersonation() {
		return (this.mApiUserSession || {}).isCustomerImpersonation;
	}

	public get canAccessSupport() {
		return (this.mApiUserSession || {}).canAccessSupport ?? false;
	}

	public get sessionReplayOptOut() {
		return (this.mApiUserSession || {}).sessionReplayOptOut ?? false;
	}

	get isAdmin() {
		return this.mApiUserSession.user?.role?.toLocaleLowerCase()?.includes('admin');
	}

	public get sendOnBehalfIsEnabled() {
		return this.mApiUserSession.account?.preferences?.sendOnBehalfEnabled;
	}

	public get canSendOnBehalf() {
		if (!this.sendOnBehalfIsEnabled) {
			return false;
		}

		return (
			this.defaultSendOnBehalfPermissions?.filter(x => x.sender?.mode !== Api.SendEmailFrom.CurrentUser).length > 0 ||
			this.customSendOnBehalfPermissions?.length > 0
		);
	}

	public get defaultSendOnBehalfPermissions() {
		return this.mApiUserSession.user.defaultSendOnBehalfPermissions;
	}

	public get customSendOnBehalfPermissions() {
		return this.mApiUserSession.user.customSendOnBehalfPermissions;
	}

	public get canSendOnBehalfWithoutFilters() {
		return (
			this.defaultSendOnBehalfPermissions?.filter(x => x.sender?.mode !== Api.SendEmailFrom.CurrentUser).length > 0 ||
			this.customSendOnBehalfPermissions?.filter(x => !x.requiredFilter).length > 0
		);
	}

	public get levitateUIStimulant() {
		return this.mApiUserSession?.levitateUIStimulant ?? Api.LevitateUIStimulant.Caffeinated;
	}

	public get supportedPluginFeatures() {
		return this.mSupportedPluginFeatures;
	}

	public setSupportedPluginFeatures(features: Api.ISupportedPluginFeatures) {
		this.mSupportedPluginFeatures = features;
	}

	public resolvePendingAction(resolvedAction: Api.PendingActions) {
		this.mApiUserSession = {
			...this.mApiUserSession,
			pendingActions: (this.mApiUserSession.pendingActions || []).filter(a => a !== resolvedAction),
		};
	}

	public clearPendingActions = () => {
		if (this.mUserSession) {
			this.mApiUserSession = {
				...this.mApiUserSession,
				pendingActions: [],
			};
		}
	};

	public removePendingAccountAction = (id: string) => {
		if (this.mUserSession) {
			this.mApiUserSession = {
				...this.mApiUserSession,
				pendingAccountActions: this.pendingAccountActions.filter(a => a.id !== id),
			};
		}
	};

	public get userRole() {
		if (!this.user) {
			return 'user';
		}

		if (this.user.role) {
			return this.user.role;
		}

		return this.user.groups.indexOf('admin') > -1 ? 'admin' : 'user';
	}

	public get featurePermissions() {
		return this.user.userGroupPermissions.featurePermissions;
	}

	public get hasAnyEmailPendingActions() {
		return this.needsToReconnectEmail || this.needsEmailConnect;
	}

	public get needsToReconnectEmail() {
		return this.pendingActions.indexOf(Api.PendingActions.ReconnectEmail) > -1;
	}

	public get needsEmailConnect() {
		return (
			this.pendingActions.indexOf(Api.PendingActions.ConnectEmailInitial) > -1 ||
			this.pendingActions.indexOf(Api.PendingActions.ConnectEmailInvalidateAccess) > -1
		);
	}

	/** Main login call */
	public updateWithCredential = (request: Api.IAuthenticationRequest) => {
		return new Promise<Api.IUserSession>((resolve, reject) => {
			const onError = (error: Api.IOperationResult<Api.IAuthenticationResponse>) => {
				runInAction(() => {
					this.logout();
					this.busy = false;
					reject(error);
				});
			};

			const onSuccess = async (opResult: Api.IOperationResult<Api.IAuthenticationResponse>) => {
				if (!opResult.success) {
					onError(opResult);
					return;
				}

				/** Check for an SMS authenticator "error" that requires more info */
				const response = opResult.value;
				if (!response?.success) {
					onError(opResult);
					return;
				}

				try {
					const credentialStore = this.webServiceHelper.getCredentialStore();
					await credentialStore.setCredential(opResult.value.token);

					// Clear LaunchDarkly storage to prevent session hijacking when users switch
					clearLaunchDarklyStorage();

					const userSession = await this.getUserSessionWithCredential(opResult.value.token);
					runInAction(() => {
						this.busy = false;
						resolve(userSession);
					});
				} catch (error) {
					onError(Api.asApiError(error));
				}
			};

			this.busy = true;

			this.webServiceHelper.callWebServiceWithOperationResults<Api.IAuthenticationResponse>(
				'user/login/v2',
				'POST',
				request,
				onSuccess,
				onError,
				null,
				false
			);
		});
	};

	public logout() {
		return this.setUserSession(null);
	}

	public load() {
		if (this.isLoaded) {
			return Promise.resolve(this.mApiUserSession);
		}

		this.loading = true;
		return new Promise<Api.IUserSession>((resolve, reject) => {
			const rejectWithApiError = (error?: Api.IOperationResultNoValue) => {
				runInAction(() => {
					this.loading = false;
					reject(error);
				});
			};
			this.getStoredCredential().then(
				(credential: Api.ICredential) => {
					if (credential) {
						this.getUserSessionWithCredential(credential)
							.then(userSession => {
								runInAction(() => {
									this.loading = false;
									resolve(userSession);
								});
							})
							.catch(rejectWithApiError);
					} else {
						rejectWithApiError();
					}
				},
				e => {
					rejectWithApiError(Api.asApiError(e));
				}
			);
		});
	}

	public setUserMilestone = <T>(name: keyof Api.IUserMilestones, value: T) => {
		if (this.isAuthenticated && name && value !== null && value !== undefined) {
			return new Promise((resolve, reject) => {
				const query = toQueryStringParams({ name, value });
				this.webServiceHelper.callWebServiceWithOperationResults<Api.IUserMilestones>(
					`user/${this.user.id}/updateUserMilestone?${query}`,
					'PATCH',
					null,
					opResult => {
						if (this.user) {
							this.user.userMilestones = opResult.value;
						}
						resolve(opResult.value);
					},
					reject
				);
			});
		}

		return null;
	};

	public updateUser = (user: Api.IUser) => {
		this.mApiUserSession = {
			...(this.mApiUserSession || {}),
			user,
		};
	};

	public get configuredIntegrations() {
		return Object.keys(this.mApiUserSession?.account?.integrations || {}).filter(
			integration => this.mApiUserSession?.account?.integrations[integration as keyof Api.IAccountIntegrations]?.enabled
		) as (keyof Api.IAccountIntegrations)[];
	}

	public get hasInsuranceIntegrationConfigured() {
		const configuredIntegrations = this.configuredIntegrations;
		return configuredIntegrations.some(integration =>
			['qqCatalyst', 'eclipse', 'ams360', 'hawkSoft', 'epicCsv', 'ezLynxCsv', 'generalCsv'].includes(integration)
		);
	}

	public get hasNonProfitIntegrationConfigured() {
		const configuredIntegrations = this.configuredIntegrations;
		return configuredIntegrations.some(integration => ['donorPerfect'].includes(integration));
	}

	public get hasLegalIntegrationConfigured() {
		const configuredIntegrations = this.configuredIntegrations;
		return configuredIntegrations.some(integration => ['clio', 'myCase', 'practicePanther'].includes(integration));
	}

	private checkAndSetTimezone = () => {
		if (this.mApiUserSession && !this.mApiUserSession?.user?.userPreferences?.timeZone) {
			const newTimeZone = moment.tz.guess();
			if (newTimeZone) {
				const onFinish = (result: Api.IOperationResult<Api.IUserPreferences>) => {
					runInAction(() => {
						if (result.success) {
							this.mApiUserSession.user.userPreferences = result.value;
						}
					});
				};
				this.webServiceHelper.callWebServiceWithOperationResults<Api.IUserPreferences>(
					this.composeApiUrl({ urlPath: 'user/preferences' }),
					'PUT',
					{
						...this.mApiUserSession.user.userPreferences,
						timeZone: newTimeZone,
					},
					onFinish,
					onFinish
				);
			}
		}
	};

	private getUserSessionWithCredential = async (credential: Api.ICredential) => {
		this.loading = true;

		const operation = await this.webServiceHelper.callWebServiceAsync<Api.IUserSession>(
			'user/context',
			'GET',
			null,
			credential
		);

		if (operation.success && operation.value) {
			const userSession = operation.value;
			return runInAction(() => {
				this.checkAndSetTimezone();
				this.setUserSession(userSession);
				this.loading = false;
				return userSession;
			});
		} else {
			runInAction(() => {
				this.loading = false;
			});
			throw operation;
		}
	};

	/** Set the user and credential, update the web service helper and flag this session as authenticated */
	protected setUserSession(userSession?: Api.IUserSession) {
		if (userSession) {
			this.mApiUserSession = userSession;
			this.mAccount = new AccountViewModel(this, userSession.account);
		} else {
			const credentialStore = this.webServiceHelper.getCredentialStore();
			credentialStore.clear();

			// Clear LaunchDarkly storage on logout
			clearLaunchDarklyStorage();

			this.mApiUserSession = null;
			this.mAccount = null;
		}
	}

	protected async getStoredCredential() {
		const credential = await this.webServiceHelper.getCredentialStore().getCredential();

		// Basically check if there is any sort of value
		// We assume that the underlying SDK call is taking care of refresing the access token in result of a 401 (expiriation)
		if (credential && credential.access_token) {
			return credential;
		}

		return null;
	}

	private onRequestStart = () => {
		this.requestCount = this.requestCount + 1;
	};

	private onRequestEnd = () => {
		this.requestCount = this.requestCount - 1;
	};
}

export const getEmptyPageControllerResolvedPromise = <T>() => {
	return Bluebird.resolve<Api.IPageCollectionControllerFetchResult<T[]>>({
		fetchedFirstPage: false,
		values: [],
	});
};

export class ObservablePageCollectionControllerOld<
	TModel extends Api.IBaseApiModel = Api.IBaseApiModel,
	TItem extends Api.IBaseApiModel = Api.IBaseApiModel,
> {
	public fetching: boolean;
	public fetchResults: TItem[];
	protected controller: Api.PageCollectionController<TModel>;
	protected fetchPromise: Bluebird<Api.IPageCollectionControllerFetchResult<TModel[]>>;
	protected transformer: (model: TModel) => TItem;

	constructor(
		client: Api.WebServiceHelper,
		apiPath: string,
		apiParams?: Api.IDictionary,
		transformer?: (model: TModel) => TItem
	) {
		makeObservable(this, {
			fetching: observable,
			fetchResults: observable.ref,
			reset: action,
			getNext: action,
			removeItems: action,
			insertItems: action,
			cancelCurrentFetch: action,
		});

		this.controller = new Api.PageCollectionController<TModel>(client, apiPath, apiParams);
		this.transformer =
			transformer ||
			(model => {
				return model as any;
			});
	}

	public get totalCount() {
		return this.controller.getTotalCount();
	}

	public reset = () => {
		this.cancelCurrentFetch();
		this.controller.reset();
		this.fetching = false;
		this.fetchResults = null;
	};

	public getNext(sortDescriptor?: Api.IPagedResultFetchContext, pageSize?: number, params?: any) {
		if (!sortDescriptor) {
			sortDescriptor = {
				sort: 'asc',
			};
		}

		this.cancelCurrentFetch();
		if (this.controller.hasAllPages(sortDescriptor, pageSize, params)) {
			return getEmptyPageControllerResolvedPromise<TModel>();
		}

		this.fetching = true;
		const promise = this.controller.getNext(sortDescriptor, pageSize, params);

		const onFinish = () => {
			if (promise === this.fetchPromise) {
				this.fetchPromise = null;
			}
			this.fetching = false;
		};

		promise
			.then(result => {
				runInAction(() => {
					const fetchResultsDictionary: Api.IDictionary<TItem> = {};
					let fetchResults = result.values.map(x => {
						const transformed = this.transformer(x);
						if (transformed) {
							fetchResultsDictionary[transformed.id] = transformed;
						}
						return transformed;
					});

					// note: we use fetchResultsDictionary to remove items in this.fetchResults that have a matching id with an item in the transformed result.values collection
					fetchResults = result.fetchedFirstPage
						? fetchResults
						: fetchResults.length > 0
							? [...this.fetchResults.filter(x => !fetchResultsDictionary[x.id]), ...fetchResults]
							: this.fetchResults;

					this.fetchResults = fetchResults;
					onFinish();
				});
			})
			.catch(() => {
				runInAction(() => {
					this.fetchResults = [];
					onFinish();
				});
			});
		this.fetchPromise = promise;
		return promise;
	}

	public removeItems = (items: TItem[]) => {
		if (this.fetchResults) {
			const indexes = this.fetchResults
				.map(x => {
					const index = items.indexOf(x);
					if (index >= 0) {
						return index;
					}
					return null;
				})
				.filter(x => x);
			this.controller.removeItems(indexes);
			this.fetchResults = this.fetchResults.filter(x => items.indexOf(x) < 0);
		}
	};

	public insertItems = (items: Api.IPageCollectionControllerInsert<TModel>[]) => {
		this.controller.insertItems(items);
		const fetchResultsDictionary = (this.fetchResults || []).reduce<Api.IDictionary<TItem>>((result, x) => {
			if (x.id) {
				result[x.id] = x;
			}
			return result;
		}, {});

		// need to loop over the models and create transformed versions in this.fetchResults
		const fetchResults: TItem[] = [];
		(this.controller.fetchResults || []).forEach(x => {
			fetchResults.push(fetchResultsDictionary[x.id] || this.transformer(x));
		});
		this.fetchResults = fetchResults;
	};

	public cancelCurrentFetch() {
		if (this.fetchPromise) {
			this.fetchPromise.cancel();
			this.fetchPromise = null;
		}
		this.fetching = false;
	}
}
